import { formatDate as ngFormatDate } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { AppLanguage, APP_LANGUAGES, DEFAULT_LANGUAGE } from 'app/config/languages.config';

export class Utils {
	/**
	 * Kept in sync with TranslateService.currentLang by translationInitializer,
	 * at bootstrap and on every language change. Static utilities have no DI,
	 * so this is how they read the active language — NOT localStorage: that
	 * key is only ever written when the user manually picks a language from
	 * the navbar, so on first load (or for any user who never touched the
	 * switcher) it stays null while the app may already be running in a
	 * different language via browser-language detection. Reading it here
	 * would silently mis-detect the language for exactly those users.
	 */
	static currentLang: string = DEFAULT_LANGUAGE.code;

	/** Set once by translationInitializer, which already has the singleton
	 * TranslateService via DI — lets static utilities (like resolveEntityName
	 * below) call `.instant()` without needing their own injection context. */
	static translateService: TranslateService;

	static getAppLanguage(code?: string): AppLanguage {
		return APP_LANGUAGES.find(l => l.code === code) || DEFAULT_LANGUAGE;
	}

	/** Uppercases a UUID (the convention this app's vocabulary system keys
	 * dynamic entity names by); leaves any other id shape untouched. */
	static formatId(id: any): string {
		if (!id) return '';
		const str = String(id);
		return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(str)
			? str.toUpperCase()
			: str;
	}

	/**
	 * Universities, schools, activities, scientific sectors, lessons, and
	 * every other `MultilingualEntity` store their bilingual `name` in the
	 * vocabulary system, keyed by their own (uppercased) UUID — the plain
	 * `.name` field returned by the API is just whichever language it was
	 * entered in. Resolves the translated name for the active language,
	 * falling back to the raw name if no translation exists for this entity.
	 */
	static resolveEntityName(entity: { id?: string; name?: string } | string | null | undefined): string {
		if (!entity) return '';
		if (typeof entity === 'string') return entity;
		const rawName = entity.name ?? '';
		if (!entity.id || !Utils.translateService) return rawName;
		const key = Utils.formatId(entity.id);
		const translated = Utils.translateService.instant(key);
		return translated && translated !== key ? translated : rawName;
	}

	/**
	 * A fixed sample instant used only to DETECT which Angular token an Intl
	 * part maps to — never to read an actual formatted value. Every numeric
	 * field is a single digit (5), so a caller-requested `'2-digit'` field
	 * ("05", 2 chars) is unambiguously distinguishable from a `'numeric'`
	 * one ("5", 1 char) regardless of what today's real date happens to be.
	 * Using `new Date()` here instead would silently mis-detect padding on
	 * any day/month/hour/minute/second that happens to already be >= 10.
	 */
	private static readonly PATTERN_PROBE_DATE = new Date(2026, 0, 5, 5, 5, 5);

	/** Memoized `buildPattern` results, keyed by `${locale}|${JSON.stringify(options)}`.
	 * The result depends only on the locale, the options and the constant probe
	 * date, and the locale is part of the key so a language change still resolves
	 * to the right pattern. Avoids rebuilding an Intl formatter on every call,
	 * which matters when a wrapper runs per row on each change-detection cycle. */
	private static readonly patternCache = new Map<string, string>();

	/**
	 * Builds an Angular-DatePipe-style pattern for ANY combination of
	 * `Intl.DateTimeFormatOptions`, for the active language — the single
	 * mechanism behind `getDateFormat()`/`getTimeFormat()` and every other
	 * locale-aware pattern this class produces, instead of a per-language
	 * table to maintain and get subtly wrong (a first attempt at just the
	 * date assumed every non-English language uses dd/MM/yyyy, which is
	 * close but wrong for German: day-first too, but period-separated —
	 * 17.09.2026, not 17/09/2026 — and the day/month/year *order* for a
	 * spelled-out date like "17 settembre 2026" isn't even the same order
	 * as "September 17, 2026").
	 *
	 * `Intl.DateTimeFormat(locale, options).formatToParts()` reports the
	 * locale's real field order, separators, and punctuation; this reads
	 * that structure back into the closest Angular format tokens.
	 */
	private static buildPattern(options: Intl.DateTimeFormatOptions): string {
		const locale = Utils.getAppLanguage(Utils.currentLang).localeId;
		const cacheKey = `${locale}|${JSON.stringify(options)}`;
		const cached = Utils.patternCache.get(cacheKey);
		if (cached !== undefined) return cached;

		let pattern: string;
		try {
			const parts = new Intl.DateTimeFormat(locale, options).formatToParts(Utils.PATTERN_PROBE_DATE);
			const is12Hour = parts.some(p => p.type === 'dayPeriod');
			const tokenByPartType: Record<string, (part: Intl.DateTimeFormatPart) => string> = {
				weekday: () => 'EEEE',
				day: p => (p.value.length >= 2 ? 'dd' : 'd'),
				month: p => (/^\d+$/.test(p.value) ? (p.value.length >= 2 ? 'MM' : 'M') : 'MMMM'),
				year: p => (p.value.length <= 2 ? 'yy' : 'yyyy'),
				hour: () => (is12Hour ? 'hh' : 'HH'),
				minute: () => 'mm',
				second: () => 'ss',
				dayPeriod: () => 'a',
			};
			pattern = parts.map(p => (tokenByPartType[p.type] ? tokenByPartType[p.type](p) : p.value)).join('');
		} catch {
			// Cached too: an invalid locale/options combination fails the same way every time.
			pattern = '';
		}
		Utils.patternCache.set(cacheKey, pattern);
		return pattern;
	}

	/** Numeric day/month/4-digit-year, e.g. 'dd/MM/yyyy', 'MM/dd/yyyy', 'dd.MM.yyyy'. */
	static getDateFormat(): string {
		return Utils.buildPattern({ year: 'numeric', month: '2-digit', day: '2-digit' }) || 'dd/MM/yyyy';
	}

	/** Same as `getDateFormat()` but with a 2-digit year, for compact displays
	 * that intentionally omit the century (e.g. 'dd/MM/yy'). */
	static getShortYearDateFormat(): string {
		return Utils.buildPattern({ year: '2-digit', month: '2-digit', day: '2-digit' }) || 'dd/MM/yy';
	}

	/** Day + month only, no year — e.g. 'dd/MM'. */
	static getDayMonthFormat(): string {
		return Utils.buildPattern({ month: '2-digit', day: '2-digit' }) || 'dd/MM';
	}

	/** Year only, e.g. 'yyyy'. */
	static getYearFormat(): string {
		return Utils.buildPattern({ year: 'numeric' }) || 'yyyy';
	}

	/**
	 * The clock: English conventionally uses a 12-hour clock with an AM/PM
	 * marker ("05:30 PM"), while it/fr/es/de/pt all use a 24-hour clock
	 * ("17:30") — a hardcoded `HH:mm` default would show "17:30" in English
	 * too, which isn't what an English-reading user expects. Pass
	 * `withSeconds` for a live-clock-style 'HH:mm:ss' / 'hh:mm:ss a'.
	 */
	static getTimeFormat(withSeconds = false): string {
		const options: Intl.DateTimeFormatOptions = withSeconds
			? { hour: '2-digit', minute: '2-digit', second: '2-digit' }
			: { hour: '2-digit', minute: '2-digit' };
		return Utils.buildPattern(options) || (withSeconds ? 'HH:mm:ss' : 'HH:mm');
	}

	/** Weekday + day + spelled-out month + year, in the language's own field
	 * order — e.g. "giovedì 17 settembre 2026" (it) vs "Thursday, September
	 * 17, 2026" (en): the order itself differs per language, not just the
	 * words, so this can't be a single hardcoded pattern with translated
	 * month names spliced in. */
	static getLongDateFormat(): string {
		return (
			Utils.buildPattern({ weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) ||
			'EEEE d MMMM yyyy'
		);
	}

	static formatDate(value: any, format = Utils.getDateFormat(), defaultValue = '-'): string {
		if (!value) return defaultValue;
		const locale = Utils.getAppLanguage(Utils.currentLang).localeId;
		try {
			return ngFormatDate(value, format, locale);
		} catch {
			return String(value);
		}
	}

	static formatDateTime(value: any, format = `${Utils.getDateFormat()} ${Utils.getTimeFormat()}`, defaultValue = '-'): string {
		return Utils.formatDate(value, format, defaultValue);
	}

	static b64toBlob(b64Data, contentType = '', sliceSize = 512) {
		const byteCharacters = atob(b64Data);
		const byteArrays = [];

		for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
			const slice = byteCharacters.slice(offset, offset + sliceSize);

			const byteNumbers = new Array(slice.length);
			for (let i = 0; i < slice.length; i++) {
				byteNumbers[i] = slice.charCodeAt(i);
			}

			const byteArray = new Uint8Array(byteNumbers);
			byteArrays.push(byteArray);
		}

		return new Blob(byteArrays, { type: contentType });
	}

	static capitalizeFirst(text: string): string {
		if (!text) return '';
		return text.charAt(0).toUpperCase() + text.slice(1);
	}

	static capitalize(text: string): string {
		if (!text) return '';
		return text
			.split(' ')
			.map(word => this.capitalizeFirst(word))
			.join(' ');
	}

	static getEntityKeyFromUrl(): string {
		const urlSegments = window.location.pathname.split('/');
		// Esempio URL: /universities/list → entityKey = 'universities'
		return urlSegments[1] || '';
	}

	static isPeriodEnded(endDateStr: string | Date | undefined | null): boolean {
		if (!endDateStr) return false;
		const endDate = new Date(endDateStr);
		endDate.setHours(23, 59, 59, 999);
		return endDate.getTime() < new Date().getTime();
	}

	static pairedRowComparator(valueA: any, valueB: any, nodeA: any, nodeB: any, isDescending: boolean): number {
		const isRowPaired = (node: any): boolean => {
			const data = node?.data;
			if (!data) return false;
			if (data.paired) return true;

			return Object.keys(data).some(key => {
				const keyLower = key.toLowerCase();
				return (
					keyLower === 'paired' ||
					keyLower.endsWith('paired') ||
					keyLower === 'pairing' ||
					keyLower.endsWith('pairing')
				) && !!data[key];
			});
		};

		const pairedA = isRowPaired(nodeA);
		const pairedB = isRowPaired(nodeB);

		if (pairedA && !pairedB) {
			return isDescending ? 1 : -1;
		}
		if (!pairedA && pairedB) {
			return isDescending ? -1 : 1;
		}

		const strA = (valueA || '').toString();
		const strB = (valueB || '').toString();
		return strA.localeCompare(strB, undefined, { numeric: true, sensitivity: 'base' });
	}
}
